/**
 * @fileoverview 高频市场卡片组件
 * @description 专为高频竞猜市场设计，突出倒计时、基准价格、涨跌概率
 * 设计风格：金色边框发光效果，脉冲动效，实时倒计时，LIVE 角标
 * @module components/market/HighFreqMarketCard
 */

import { TrendingDown, TrendingUp, Zap } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'wouter';
import { getAssetIconUrl, formatCountdownDHMS } from '../../lib/utils';
import { fetchClosePrice } from '../../services/marketService';
import { API_OK } from '../../services/api';
import type { Market } from '../../types';

/** 倒计时组件 */
function Countdown({ endTime }: { endTime: string }) {
  const { t } = useTranslation();
  const [seconds, setSeconds] = useState<number>(0);

  // endTime 为空或为 1970 年（原始值为 0）时表示休市
  const isValid = !!endTime && new Date(endTime).getFullYear() > 1971;

  useEffect(() => {
    if (!isValid) { setSeconds(0); return; }
    const calc = () => {
      const diff = Math.max(0, Math.floor((new Date(endTime).getTime() - Date.now()) / 1000));
      setSeconds(diff);
    };
    calc();
    const timer = setInterval(calc, 1000);
    return () => clearInterval(timer);
  }, [endTime, isValid]);

  if (!isValid) {
    return (
      <span className="font-mono text-base font-bold tabular-nums text-muted-foreground/60">
        {t('market.card.closed')}
      </span>
    );
  }

  const isUrgent = seconds < 60;

  return (
    <span
      className="font-mono text-base font-bold tabular-nums"
      style={{ color: isUrgent ? '#F6465D' : '#F0B90B' }}
    >
      {formatCountdownDHMS(seconds)}
    </span>
  );
}

interface HighFreqMarketCardProps {
  market: Market;
  animationDelay?: number;
  /** 用户是否正在参与此市场（有未结算订单） */
  isParticipating?: boolean;
}

export default function HighFreqMarketCard({ market, animationDelay = 0, isParticipating = false }: HighFreqMarketCardProps) {
  const { t } = useTranslation();
  const basePrice = market.basePrice ?? market.targetPrice ?? 0;
  const [livePrice, setLivePrice] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchPrice = async () => {
      try {
        const resp = await fetchClosePrice(market.id);
        if (!cancelled && resp.status === API_OK && resp.data) {
          const price = resp.data.price;
          if (price !== null && price !== undefined && Number.isFinite(Number(price))) {
            setLivePrice(Number(price));
          }
        }
      } catch { /* 静默失败 */ }
    };
    fetchPrice();
    const interval = setInterval(fetchPrice, 10_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [market.id]);

  const diff = livePrice !== null ? livePrice - basePrice : 0;
  const isUp = diff >= 0;
  const diffPercent = (basePrice > 0 && livePrice !== null)
    ? ((diff / basePrice) * 100).toFixed(2)
    : null;

  return (
    <Link href={`/markets/${market.id}`}>
      <div
        className="group relative flex flex-col h-full rounded-xl p-4 cursor-pointer animate-fade-in-up transition-all duration-200 hover:-translate-y-0.5"
        style={{
          animationDelay: `${animationDelay}ms`,
          background: 'linear-gradient(135deg, #1E2026 0%, #16181C 100%)',
          border: '1px solid rgba(240,185,11,0.2)',
          boxShadow: '0 0 16px rgba(240,185,11,0.04)',
        }}
      >
        {/* 悬停光晕 */}
        <div
          className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
          style={{ boxShadow: '0 0 24px rgba(240,185,11,0.12)', background: 'radial-gradient(ellipse at top, rgba(240,185,11,0.05) 0%, transparent 70%)' }}
        />
        {/* 头部：图标 + 角标组（LIVE / Closed + 参与中） */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 overflow-hidden"
              style={{ background: 'rgba(240,185,11,0.1)', border: '1px solid rgba(240,185,11,0.2)' }}
            >
              <img src={getAssetIconUrl(market.assetSymbol, market.category)} alt={market.assetSymbol || market.category} className="w-5 h-5 object-contain" />
            </div>
            <div className="leading-none">
              <span className="text-[11px] text-brand font-mono font-bold block mb-0.5">
                {market.assetSymbol}
              </span>
              <span className="text-[10px] text-muted-foreground/70">{t('market.detail.highFreqBadge')}</span>
            </div>
          </div>
          {/* 右侧角标组：参与中 + LIVE/Closed */}
          <div className="flex items-center gap-1.5">
            {isParticipating && (
              <div
                className="flex items-center gap-1 px-2 py-0.5 rounded-full"
                style={{
                  background: 'rgba(14,203,129,0.15)',
                  border: '1px solid rgba(14,203,129,0.35)',
                }}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#0ECB81] animate-pulse" />
                <span className="text-[10px] font-bold text-[#0ECB81]">{t('marketBadge.participating')}</span>
              </div>
            )}
            {market.status === 'closed' ? (
              <div
                className="flex items-center gap-1 px-2 py-0.5 rounded-full"
                style={{ background: 'rgba(150,150,150,0.12)', border: '1px solid rgba(150,150,150,0.25)' }}
              >
                <span className="text-[10px] font-bold text-muted-foreground">{t('market.card.closed')}</span>
              </div>
            ) : (
              <div
                className="flex items-center gap-1 px-2 py-0.5 rounded-full"
                style={{ background: 'rgba(240,185,11,0.12)', border: '1px solid rgba(240,185,11,0.25)' }}
              >
                <Zap className="h-2.5 w-2.5 text-brand" />
                <span className="text-[10px] font-bold text-brand">LIVE</span>
              </div>
            )}
          </div>
        </div>

        {/* 标题 */}
        <h3
          className="text-sm font-semibold text-foreground/90 leading-snug mb-3 line-clamp-2 min-h-[2.5rem] group-hover:text-white transition-colors"
          style={{ fontFamily: 'Space Grotesk, sans-serif' }}
        >
          {market.title}
        </h3>

        {/* 价格区域 */}
        <div className="flex items-center justify-between mb-3 py-2 px-3 rounded-lg bg-white/3 border border-white/5">
          <div>
            <p className="text-[10px] text-muted-foreground/70 mb-0.5">{t('market.detail.basePrice')}</p>
            <p className="text-xs font-mono font-bold text-white">
              {basePrice > 0
                ? `$${basePrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : <span className="text-muted-foreground">--</span>
              }
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-muted-foreground/70 mb-0.5">{t('market.detail.currentShort')}</p>
            {livePrice === null ? (
              <span className="text-[10px] text-muted-foreground animate-pulse">{t('market.detail.fetching')}</span>
            ) : (
              <div className="flex items-center gap-1 justify-end">
                {isUp
                  ? <TrendingUp className="h-3 w-3 text-[#0ECB81]" />
                  : <TrendingDown className="h-3 w-3 text-[#F6465D]" />
                }
                <span
                  className="text-xs font-mono font-bold"
                  style={{ color: isUp ? '#0ECB81' : '#F6465D' }}
                >
                  {isUp ? '+' : ''}{diffPercent}%
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Yes/No 概率条 */}
        <div className="space-y-1.5 mb-3">
          <div className="flex items-center justify-between text-[11px]">
            <span className="text-[#0ECB81] font-bold font-mono">{t('market.card.up')} {market.yesPercent}%</span>
            <span className="text-[#F6465D] font-bold font-mono">{market.noPercent}% {t('market.card.down')}</span>
          </div>
          <div className="h-1 rounded-full overflow-hidden bg-white/5 flex">
            <div
              className="progress-yes rounded-l-full transition-all duration-500"
              style={{ width: `${market.yesPercent}%` }}
            />
            <div
              className="progress-no rounded-r-full transition-all duration-500"
              style={{ width: `${market.noPercent}%` }}
            />
          </div>
        </div>

        {/* 底部：倒计时 + 参与人数 */}
        <div className="flex items-center justify-between pt-2.5 border-t border-white/5">
          <div>
            <p className="text-[10px] text-muted-foreground/70 mb-0.5">{t('market.card.countdown')}</p>
            <Countdown endTime={market.endTime} />
          </div>
          <div className="text-right">
            <p className="text-[10px] text-muted-foreground/70 mb-0.5">{t('market.card.participants')}</p>
            <p className="text-sm font-mono font-bold text-white">{market.participantCount}</p>
          </div>
        </div>
      </div>
    </Link>
  );
}
